import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Loader2, CheckCircle2, ArrowRight, Check, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { api } from "../lib/api";

/** Kept in sync with the backend Stripe catalog (setup_stripe.py). */
const TIERS = [
  {
    key: "essentiel",
    name: "Essentiel",
    monthly: 19.99,
    yearly: 199.90,
    yearlyEquiv: 16.66,
    features: [
      "10 factures analysées / mois",
      "50 produits",
      "10 fiches recettes",
      "Historique des prix sur 6 mois",
      "Éditeur de menu · QR code · Impression",
    ],
    highlight: false,
  },
  {
    key: "pro",
    name: "Pro",
    monthly: 34.99,
    yearly: 349.90,
    yearlyEquiv: 29.16,
    features: [
      "25 factures analysées / mois",
      "150 produits",
      "25 fiches recettes",
      "Historique des prix sur 12 mois",
      "Négociation personnalisée",
      "Éditeur de menu · QR code · Impression",
    ],
    highlight: false,
    badge: "Le plus choisi",
  },
  {
    key: "integral",
    name: "Intégral",
    monthly: 49.99,
    yearly: 499.90,
    yearlyEquiv: 41.66,
    features: [
      "Factures analysées illimitées",
      "Produits illimités",
      "Fiches recettes illimitées",
      "Historique des prix illimité",
      "Négociation personnalisée",
      "Éditeur de menu · QR code · Impression",
      "Traduction du menu en 7 langues",
    ],
    note: "Utilisation illimitée dans le cadre d'un usage raisonnable pour un établissement.",
    highlight: true,
  },
];

const fmt = (n) => n.toFixed(2).replace(".", ",");

/** Map a raw lookup_key (e.g. "pro_monthly") to { tier, interval }. */
function decodeLookup(key) {
  if (!key) return null;
  const [tier, interval] = key.split("_");
  if (!tier || !interval) return null;
  return { tier, interval };
}

export default function SubscriptionCard() {
  const [search] = useSearchParams();
  const [billing, setBilling] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [selectedTier, setSelectedTier] = useState("pro");
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const autoStart = search.get("checkout") === "1";

  // Pre-select from sessionStorage (Signup) or ?plan= query (deep links from Landing).
  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem("rs_selected_plan"); } catch { /* ignore */ }
    if (!raw) raw = search.get("plan");
    const decoded = decodeLookup(raw);
    if (decoded) {
      setSelectedTier(decoded.tier);
      setAnnual(decoded.interval === "yearly");
    }
  }, [search]);

  useEffect(() => {
    (async () => {
      try {
        const b = await api.billingMe();
        setBilling(b);
      } catch { /* ignore */ }
    })();
  }, []);

  const currentLookup = `${selectedTier}_${annual ? "yearly" : "monthly"}`;

  const startCheckout = async (lookup_key) => {
    setLoading(true);
    try {
      const r = await api.billingCheckout(lookup_key);
      if (r.checkout_url) window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de démarrer le paiement.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoStart && billing && billing.subscription_status !== "active") {
      startCheckout(currentLookup);
      try { sessionStorage.removeItem("rs_selected_plan"); } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, billing]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.billingPortal();
      if (r.portal_url) window.location.href = r.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Portail indisponible.");
    } finally { setPortalLoading(false); }
  };

  const isActive = billing?.subscription_status === "active";
  const daysLeft = billing?.trial_days_left ?? null;
  const activePlanMeta = useMemo(() => {
    const dec = decodeLookup(billing?.subscription_plan);
    return dec ? { ...dec, name: TIERS.find((t) => t.key === dec.tier)?.name || dec.tier } : null;
  }, [billing]);

  return (
    <Card className="rounded-lg border-[#CDD7E3] overflow-hidden relative" data-testid="subscription-card">
      {/* Deep-blue banner top */}
      <div className="relative bg-[#0B1F33] text-white px-5 py-4 overflow-hidden">
        <div aria-hidden className="absolute inset-0 pat-barcode opacity-50" />
        <div className="relative flex items-center gap-2 flex-wrap">
          <CreditCard className="w-4 h-4 text-[#FFD166]" />
          <h3 className="font-heading font-bold text-white text-[15px]">Abonnement</h3>
          {isActive ? (
            <Badge className="bg-[#E4F4EA] text-[#1B5C39] border-[#C0E1CC] hover:bg-[#E4F4EA]">
              Actif{activePlanMeta ? ` · ${activePlanMeta.name}` : ""}
            </Badge>
          ) : daysLeft != null ? (
            <Badge className="bg-[#FFF0D2] text-[#7A4C0B] border-[#FFD166] hover:bg-[#FFF0D2]">
              Essai · {daysLeft} j restant{daysLeft > 1 ? "s" : ""}
            </Badge>
          ) : (
            <Badge className="bg-white/10 text-white border-white/20 hover:bg-white/10">Essai gratuit</Badge>
          )}
        </div>
        <p className="mt-1 text-[12.5px] text-white/75 relative">
          {isActive
            ? "Votre abonnement est actif. Facturation automatique par Stripe."
            : "Trois formules à partir de 19,99 € HT/mois — 14 jours gratuits, sans carte bancaire."}
        </p>
      </div>

      <CardContent className="p-5">
        {!isActive && (
          <>
            {/* Billing toggle */}
            <div className="flex justify-center mb-4">
              <div
                role="tablist"
                aria-label="Fréquence de facturation"
                data-testid="sub-toggle"
                className="relative inline-flex items-center rounded-full bg-[#F4F5EF] border border-[#CDD7E3] p-1"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={!annual}
                  data-testid="sub-toggle-monthly"
                  onClick={() => setAnnual(false)}
                  className={`px-4 h-8 text-[12.5px] font-semibold rounded-full transition-colors ${
                    !annual ? "bg-[#0B1F33] text-white" : "text-[#0B1F33]"
                  }`}
                >
                  Mensuel
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={annual}
                  data-testid="sub-toggle-yearly"
                  onClick={() => setAnnual(true)}
                  className={`px-4 h-8 text-[12.5px] font-semibold rounded-full inline-flex items-center gap-2 transition-colors ${
                    annual ? "bg-[#0B1F33] text-white" : "text-[#0B1F33]"
                  }`}
                >
                  Annuel
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[#FF9F1C] text-[#0B1F33] text-[9.5px] font-extrabold uppercase tracking-wide">
                    2 mois offerts
                  </span>
                </button>
              </div>
            </div>

            {/* Tier cards (mobile order: Pro, Essentiel, Intégral) */}
            <div className="flex flex-col md:grid md:grid-cols-3 gap-3 mb-4 pt-3">
              {TIERS.map((t) => {
                const isSelected = selectedTier === t.key;
                const shownPrice = annual ? t.yearlyEquiv : t.monthly;
                const orderClass =
                  t.key === "pro" ? "order-1 md:order-2"
                  : t.key === "essentiel" ? "order-2 md:order-1"
                  : "order-3 md:order-3";
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setSelectedTier(t.key)}
                    data-testid={`sub-tier-${t.key}`}
                    className={`${orderClass} relative text-left rounded-md p-4 transition-all flex flex-col ${
                      t.highlight ? "pt-8 overflow-hidden" : ""
                    } ${
                      isSelected
                        ? t.highlight
                          ? "border border-[#FF9F1C] bg-[#FFF0D2]/60 ring-2 ring-[#FF9F1C]/60 ring-offset-2 ring-offset-white"
                          : t.badge
                            ? "border-2 border-[#FF9F1C] bg-[#FFF0D2]/60 ring-2 ring-[#FF9F1C]/30"
                            : "border border-[#FF9F1C] bg-[#FFF0D2]/60 ring-2 ring-[#FF9F1C]/30"
                        : t.highlight
                          ? "border border-[#FF9F1C] bg-white ring-2 ring-[#FFD166]/40 ring-offset-2 ring-offset-white"
                          : t.badge
                            ? "border-2 border-[#FF9F1C] bg-white shadow-[0_6px_18px_rgba(255,159,28,0.10)] hover:bg-[#FFF0D2]/30"
                            : "border border-[#CDD7E3] bg-white hover:border-[#1769E0]"
                    }`}
                  >
                    {t.highlight && (
                      <>
                        <motion.span
                          aria-hidden
                          className="pointer-events-none absolute -inset-1 rounded-md opacity-30 blur-xl"
                          style={{
                            background:
                              "conic-gradient(from 0deg, rgba(255,159,28,0.28), rgba(255,209,102,0.10), rgba(255,159,28,0.28), rgba(255,209,102,0.10), rgba(255,159,28,0.28))",
                          }}
                          initial={{ rotate: 0 }}
                          animate={{ rotate: 360 }}
                          transition={{ duration: 12, ease: "linear", repeat: Infinity }}
                        />
                        <div
                          data-testid="sub-badge-best-offer"
                          className="absolute top-0 left-0 right-0 flex items-center justify-center gap-1.5 h-6 bg-gradient-to-r from-[#F58F06] via-[#FFB84D] to-[#F58F06] text-[#0B1F33] text-[9.5px] font-extrabold uppercase tracking-[0.14em] overflow-hidden"
                        >
                          <Sparkles className="w-3 h-3 relative z-10" strokeWidth={2.8} />
                          <span className="relative z-10">Meilleure offre</span>
                          <motion.span
                            aria-hidden
                            className="absolute inset-y-0 w-1/3"
                            style={{
                              background:
                                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.65) 50%, transparent 100%)",
                            }}
                            initial={{ x: "-120%" }}
                            animate={{ x: "220%" }}
                            transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity, repeatDelay: 1.8 }}
                          />
                        </div>
                      </>
                    )}
                    {!t.highlight && t.badge && (
                      <span
                        data-testid="sub-badge-most-chosen"
                        className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center px-2 py-0.5 rounded-full bg-[#FF9F1C] text-[#0B1F33] text-[9.5px] font-extrabold uppercase tracking-wide shadow-sm whitespace-nowrap"
                      >
                        {t.badge}
                      </span>
                    )}
                    <div className="relative z-10 flex flex-col flex-1">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E6875] font-semibold">
                        {t.name}
                      </div>
                      <div className="mt-1 text-[20px] font-extrabold text-[#111111] font-heading tabular-nums">
                        {fmt(shownPrice)} €
                        <span className="text-[11px] text-[#5E6875] font-normal ml-1">HT/mois</span>
                      </div>
                      <div className="text-[11px] text-[#5E6875] min-h-[15px]">
                        {annual ? <>facturé {fmt(t.yearly)} €/an</> : "sans engagement"}
                      </div>
                      <ul className="mt-2.5 space-y-1 text-[11.5px] text-[#0B1F33] flex-1">
                        {t.features.map((f) => (
                          <li key={f} className="flex items-start gap-1.5">
                            <Check className="w-3 h-3 mt-0.5 shrink-0 text-[#FF9F1C]" strokeWidth={3} />
                            <span className="leading-snug">{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                data-testid="subscribe-btn"
                onClick={() => startCheckout(currentLookup)}
                disabled={loading}
                className="text-[#111111] gap-2 rounded-md h-10 px-5 text-[13px] font-semibold shadow-none border-0 bg-[#FF9F1C] hover:bg-[#F58F06]"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {loading ? "Redirection…" : `S'abonner à ${TIERS.find((t) => t.key === selectedTier).name}`}
              </Button>
              <span className="text-[11px] text-[#5E6875]">
                <CheckCircle2 className="inline w-3.5 h-3.5 text-[#24845B] mr-1" />
                Paiement sécurisé par Stripe · Résiliable à tout moment
              </span>
            </div>
          </>
        )}

        {isActive && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="manage-subscription-btn"
                onClick={openPortal}
                disabled={portalLoading}
                variant="outline"
                className="rounded-md border-[#CDD7E3]"
              >
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Gérer mon abonnement
              </Button>
            </div>

            {/* Résiliation programmée à la fin de la période payée.
                Bandeau + bouton unique qui bascule Résilier / Réactiver
                selon `cancel_at_period_end`. Aucune donnée n'est jamais
                supprimée — c'est une action distincte de « supprimer le
                compte ». */}
            <CancelSection billing={billing} onChanged={async () => {
              try { setBilling(await api.billingMe()); } catch {}
            }} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Section « Résilier / Réactiver mon abonnement » — visible pour tout
 * abonnement actif. Ouvre une modale de confirmation indiquant la date
 * exacte de fin d'abonnement (Stripe `current_period_end`). Aucune
 * suppression de données. Idempotent côté serveur.
 */
function CancelSection({ billing, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cancelScheduled = !!billing?.cancel_at_period_end;
  const endIso = billing?.current_period_end;
  const endTxt = endIso ? new Date(endIso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "à la fin de la période en cours";

  const doCancel = async () => {
    setBusy(true);
    try {
      const r = await api.billingCancel();
      const d = r.current_period_end ? new Date(r.current_period_end).toLocaleDateString("fr-FR") : "à la fin de la période";
      toast.success(`Votre abonnement prendra fin le ${d}`, { description: "Un e-mail de confirmation vient d'être envoyé." });
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Résiliation impossible");
    } finally { setBusy(false); }
  };

  const doReactivate = async () => {
    setBusy(true);
    try {
      await api.billingReactivate();
      toast.success("Abonnement réactivé", { description: "La résiliation programmée a été annulée." });
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Réactivation impossible");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-[#CDD7E3] bg-[#F4F5EF] p-4" data-testid="cancel-section">
      <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875] mb-1">Abonnement et facturation</div>
      {cancelScheduled ? (
        <>
          <p className="text-[13px] text-[#0B1F33] mb-3" data-testid="cancel-end-info">
            Votre abonnement prendra fin le <strong>{endTxt}</strong>. Vous conservez toutes vos fonctionnalités, factures, produits, recettes et menus jusqu'à cette date.
          </p>
          <Button
            data-testid="reactivate-subscription-btn"
            onClick={doReactivate}
            disabled={busy}
            className="rounded-md bg-[#0B1F33] hover:bg-[#0F2A45] text-white"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Réactiver mon abonnement
          </Button>
        </>
      ) : (
        <>
          <p className="text-[13px] text-[#5E6875] mb-3">
            La résiliation prend effet à la fin de la période déjà payée. Vos données ne sont pas supprimées — c'est une action distincte de la suppression du compte.
          </p>
          <Button
            data-testid="cancel-subscription-btn"
            onClick={() => setOpen(true)}
            variant="outline"
            className="rounded-md text-[#7C2A21] border-[#F5C7C7] hover:bg-[#FDECEC]"
          >
            Résilier mon abonnement
          </Button>
        </>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cancel-confirm-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmer la résiliation</DialogTitle>
          </DialogHeader>
          <div className="text-[13.5px] text-[#0B1F33] space-y-2">
            <p>Votre abonnement RentaScan restera actif jusqu'au <strong>{endTxt}</strong>.</p>
            <p>Vous conservez :</p>
            <ul className="list-disc pl-5 text-[13px] text-[#5E6875] space-y-0.5">
              <li>toutes vos factures et historiques de prix,</li>
              <li>vos produits, recettes et menus publiés,</li>
              <li>l'accès complet aux fonctionnalités de votre formule.</li>
            </ul>
            <p className="text-[12px] text-[#5E6875] italic">Vous pourrez réactiver votre abonnement à tout moment avant cette date.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="cancel-confirm-cancel">Non, garder mon abonnement</Button>
            <Button onClick={doCancel} disabled={busy} className="bg-[#7C2A21] hover:bg-[#5F1F19] text-white" data-testid="cancel-confirm-ok">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Oui, résilier à la fin de la période
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
